import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { Entete } from '../components/Entete'
import { MarqueHomonyme } from '../components/MarqueHomonyme'
import { RapportDiagnostic } from '../components/RapportDiagnostic'
import { useSession } from '../auth/session'
import { formaterDateFr } from '../lib/dates'
import { estPreproduction } from '../lib/env'
import { lienGoogleDoc } from '../lib/rapport'
import { LIBELLE_CATEGORIE, lireDossier, type FichierDossier } from '../lib/dossier'
import {
  apercuPdf,
  estPrevisualisable,
  libererApercu,
  telechargerDocument,
} from '../lib/stockage'
import { formaterTaille } from '../lib/tailles'

/**
 * Page d'un dossier.
 *
 * Elle s'ouvre PENDANT un rendez-vous, souvent en partage d'ecran. Tout y est
 * range dans un ordre fixe, rien n'apparait sans qu'on l'ait demande, et le PDF
 * contient exactement ce qui est affiche au moment ou l'on imprime.
 */
export default function Dossier() {
  const { id = '' } = useParams<{ id: string }>()
  const [parametres] = useSearchParams()
  const etat = useSession()

  // La recherche et la page d'ou l'on vient voyagent dans l'URL : un conseiller
  // qui consulte trois dossiers d'affilee ne retape pas sa recherche trois fois.
  const retour = parametres.toString() === '' ? '/' : `/?${parametres.toString()}`

  const requete = useQuery({
    queryKey: ['dossier', id],
    queryFn: () => lireDossier(id),
  })

  if (etat.statut !== 'connecte') return null

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sans-impression">
        <Entete profil={etat.profil} />
      </div>

      <main className="mx-auto max-w-5xl px-6 py-8">
        {requete.isPending ? (
          <p data-test="etat-chargement" role="status" className="text-slate-600">
            Chargement du dossier…
          </p>
        ) : requete.isError ? (
          <div
            data-test="etat-erreur"
            role="alert"
            className="rounded border border-score-actuel bg-red-50 p-4 text-slate-800"
          >
            <p className="font-semibold text-score-actuel">Le chargement a échoué.</p>
            <p className="mt-1 text-sm">
              Ce dossier n’a pas pu être lu. Ce n’est pas un dossier absent : réessayez, et
              prévenez un administrateur si cela persiste.
            </p>
            <button
              type="button"
              onClick={() => void requete.refetch()}
              className="mt-3 rounded border border-slate-300 bg-white px-3 py-1 font-medium text-slate-700 hover:bg-slate-100"
            >
              Réessayer
            </button>
          </div>
        ) : requete.data === null ? (
          <div data-test="etat-introuvable" className="rounded border border-slate-300 bg-white p-6">
            <h1 className="text-lg font-semibold text-slate-900">Dossier introuvable</h1>
            <p className="mt-2 text-slate-700">
              Ce dossier n’existe pas, ou il a été archivé.
            </p>
            <Link
              to={retour}
              className="mt-4 inline-block font-medium text-marque-primaire-texte hover:text-marque-primaire-survol"
            >
              Revenir à la liste
            </Link>
          </div>
        ) : (
          <Contenu dossier={requete.data} retour={retour} />
        )}
      </main>
    </div>
  )
}

function Contenu({
  dossier,
  retour,
}: {
  dossier: NonNullable<Awaited<ReturnType<typeof lireDossier>>>
  retour: string
}) {
  return (
    <>
      {/*
        Entete repete en tete de chaque page du PDF.

        Rattache DIRECTEMENT au <body> par un portail, et ce n'est pas un
        detail : un element `position: fixed` place a l'interieur de l'arbre
        React n'est pas repris en haut de chaque page par Chromium — il atterrit
        en bas de la premiere et chevauche le texte. Constate sur le PDF, pas
        sur un apercu ecran.
      */}
      {createPortal(
        <div className="entete-impression" aria-hidden="true">
          {/* La mention de preproduction voyage avec l'entete repete : un
              rapport imprime depuis cette instance la porte sur CHAQUE page,
              pas seulement sur la premiere. Des donnees fabriquees ne doivent
              pas pouvoir circuler sans leur avertissement. */}
          {estPreproduction(import.meta.env) ? 'PRÉPRODUCTION — données de test · ' : ''}
          {dossier.nom} {dossier.prenom} — {formaterDateFr(dossier.cree_le)}
        </div>,
        document.body,
      )}

      <header className="eviter-coupure">
        <Link
          to={retour}
          data-test="retour-liste"
          className="sans-impression text-sm font-medium text-marque-primaire-texte hover:text-marque-primaire-survol"
        >
          ← Revenir à la liste
        </Link>

        <div className="mt-3 h-1 w-16 rounded-full bg-marque-primaire" aria-hidden="true" />

        <h1 data-test="titre-dossier" className="mt-4 text-3xl font-bold text-slate-900">
          <span className="uppercase">{dossier.nom}</span> {dossier.prenom}
          <span className="sans-impression">
            <MarqueHomonyme homonyme={dossier.homonyme} />
          </span>
        </h1>
        <p className="mt-1 text-slate-600">
          Dossier créé le <span data-test="date-dossier">{formaterDateFr(dossier.cree_le)}</span>
        </p>
      </header>

      {/* Le lien de la copie Drive (serveur) prime ; celui du rapport (plugin 0.6.2) reste lu pour les dossiers deja envoyes. */}
      <Documents
        dossier={dossier}
        lienGdoc={lienGoogleDoc({ lien_google_doc: dossier.lien_google_doc }) ?? lienGoogleDoc(dossier.rapport?.donnees)}
      />

      <div className="mt-10">
        {dossier.rapport === null ? (
          <p data-test="sans-rapport" className="rounded border border-slate-300 bg-white p-4 text-slate-700">
            Aucun rapport de diagnostic n’est rattaché à ce dossier.
          </p>
        ) : (
          <RapportDiagnostic
            donnees={dossier.rapport.donnees}
            versionFormat={dossier.rapport.version_format}
          />
        )}
      </div>

      <div className="sans-impression mt-10 border-t border-slate-200 pt-6">
        <button
          type="button"
          data-test="bouton-imprimer"
          onClick={() => window.print()}
          className="rounded bg-marque-primaire-texte px-4 py-2 font-medium text-white hover:bg-marque-primaire-survol"
        >
          Imprimer ou enregistrer en PDF
        </button>
        <p className="mt-2 text-sm text-slate-600">
          Le PDF contient ce qui est affiché maintenant. Le troisième score et le détail des
          certifications sont repliés au chargement : repliés, ils en seront absents.
        </p>
      </div>
    </>
  )
}

interface Apercu {
  readonly categorie: FichierDossier['categorie']
  readonly nom: string
  readonly url: string
}

function Documents({
  dossier,
  lienGdoc,
}: {
  dossier: { id: string; fichiers: readonly FichierDossier[] }
  lienGdoc: string | null
}) {
  const [apercu, setApercu] = useState<Apercu | null>(null)
  const [enPreparation, setEnPreparation] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  // L'objet local courant, pour pouvoir le revoquer meme si le composant est
  // demonte sans passer par le bouton de fermeture.
  const objetCourant = useRef<string | null>(null)

  useEffect(() => {
    return () => {
      if (objetCourant.current !== null) libererApercu(objetCourant.current)
    }
  }, [])

  function fermer(): void {
    if (objetCourant.current !== null) {
      // Revoquer a la fermeture n'est pas une politesse : sans cela l'objet
      // reste lisible dans la page jusqu'au rechargement.
      libererApercu(objetCourant.current)
      objetCourant.current = null
    }
    setApercu(null)
  }

  async function ouvrir(fichier: FichierDossier): Promise<void> {
    if (enPreparation !== null) return
    setEnPreparation(fichier.categorie)
    setErreur(null)
    try {
      const url = await apercuPdf(dossier.id, fichier.categorie)
      if (objetCourant.current !== null) libererApercu(objetCourant.current)
      objetCourant.current = url
      setApercu({ categorie: fichier.categorie, nom: fichier.nom_fichier, url })
    } catch {
      setErreur("L'aperçu n'a pas pu être ouvert. Réessayez, ou téléchargez le document.")
    } finally {
      setEnPreparation(null)
    }
  }

  return (
    <section aria-labelledby="titre-documents" className="mt-8">
      <h2 id="titre-documents" className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        Les documents
      </h2>

      {/*
        CV en Google Docs (plugin 0.6.2) : le conseiller le modifie en direct
        pendant l'entretien, sans telecharger de Word. Le lien a ete valide et
        reconstruit par lienGoogleDoc ; les acces restent ceux du dossier
        « CV faits » du Drive.
      */}
      {lienGdoc === null ? null : (
        <a
          href={lienGdoc}
          target="_blank"
          rel="noopener noreferrer"
          data-test="ouvrir-google-docs"
          className="sans-impression mt-4 inline-flex items-center gap-2 rounded bg-marque-primaire-texte px-4 py-2 text-sm font-medium text-white hover:bg-marque-primaire-survol"
        >
          Ouvrir le CV dans Google Docs
          <span aria-hidden="true">↗</span>
          <span className="sr-only">(nouvel onglet)</span>
        </a>
      )}

      {dossier.fichiers.length === 0 ? (
        <p data-test="sans-document" className="mt-3 text-slate-700">
          Aucun document n’est rattaché à ce dossier.
        </p>
      ) : (
        <ul data-test="documents" className="mt-4 grid gap-3 md:grid-cols-3">
          {dossier.fichiers.map((fichier) => (
            <Document
              key={fichier.categorie}
              dossierId={dossier.id}
              fichier={fichier}
              onApercu={() => void ouvrir(fichier)}
              apercuEnCours={enPreparation === fichier.categorie}
            />
          ))}
        </ul>
      )}

      {erreur ? (
        <p role="alert" data-test="erreur-apercu" className="mt-3 text-sm text-score-actuel">
          {erreur}
        </p>
      ) : null}

      {apercu ? <FenetreApercu apercu={apercu} onFermer={fermer} /> : null}
    </section>
  )
}

/**
 * Apercu d'un PDF, dans la page.
 *
 * Ce qui est affiche est un objet LOCAL : l'URL signee a ete consommee au
 * moment du clic et n'entre ni dans le DOM, ni dans la barre d'adresse, ni dans
 * l'historique. La fermeture la revoque.
 */
function FenetreApercu({ apercu, onFermer }: { apercu: Apercu; onFermer: () => void }) {
  useEffect(() => {
    function surTouche(evenement: KeyboardEvent): void {
      if (evenement.key === 'Escape') onFermer()
    }
    document.addEventListener('keydown', surTouche)
    return () => document.removeEventListener('keydown', surTouche)
  }, [onFermer])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Aperçu de ${apercu.nom}`}
      data-test="fenetre-apercu"
      className="sans-impression fixed inset-0 z-50 flex flex-col bg-slate-900/70 p-4"
    >
      <div className="mx-auto flex h-full w-full max-w-5xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center gap-4 border-b border-slate-200 px-4 py-3">
          <p className="truncate font-medium text-slate-900">{apercu.nom}</p>
          <button
            type="button"
            data-test="fermer-apercu"
            onClick={onFermer}
            className="ml-auto rounded border border-slate-300 px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            Fermer
          </button>
        </div>

        <iframe
          title={`Aperçu de ${apercu.nom}`}
          data-test="cadre-apercu"
          src={apercu.url}
          className="h-full w-full rounded-b-lg"
        />
      </div>
    </div>
  )
}

function Document({
  dossierId,
  fichier,
  onApercu,
  apercuEnCours,
}: {
  dossierId: string
  fichier: FichierDossier
  onApercu: () => void
  apercuEnCours: boolean
}) {
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  async function telecharger(): Promise<void> {
    if (enCours) return
    setEnCours(true)
    setErreur(null)
    try {
      await telechargerDocument(dossierId, fichier.categorie, fichier.nom_fichier)
    } catch {
      // Un bouton qui ne fait rien en entretien est pire qu'un message d'erreur.
      setErreur('Le téléchargement a échoué. Réessayez, ou prévenez un administrateur.')
    } finally {
      setEnCours(false)
    }
  }

  return (
    <li className="eviter-coupure rounded-lg border border-slate-200 bg-white p-4">
      <p className="font-semibold text-slate-900">{LIBELLE_CATEGORIE[fichier.categorie]}</p>
      <p className="mt-1 break-words text-sm text-slate-700">{fichier.nom_fichier}</p>
      <p className="mt-1 text-sm text-slate-500">{formaterTaille(fichier.octets)}</p>

      <div className="sans-impression mt-3 flex gap-2">
        {/*
          Pas de bouton d'apercu pour un .docx — et surtout pas un bouton
          inactif. La decision se prend sur le type MIME enregistre en base,
          etabli par l'ingestion a partir des octets, jamais sur l'extension du
          nom de fichier, qui vient de l'appelant.
        */}
        {estPrevisualisable(fichier.type_mime) ? (
          <button
            type="button"
            data-test={`apercu-${fichier.categorie}`}
            disabled={apercuEnCours}
            onClick={onApercu}
            className="flex-1 rounded border border-marque-primaire-texte px-3 py-2 text-sm font-medium text-marque-primaire-texte hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {apercuEnCours ? 'Ouverture…' : 'Aperçu'}
          </button>
        ) : null}

        <button
          type="button"
          data-test={`telecharger-${fichier.categorie}`}
          disabled={enCours}
          onClick={() => void telecharger()}
          className="flex-1 rounded bg-marque-primaire-texte px-3 py-2 text-sm font-medium text-white hover:bg-marque-primaire-survol disabled:cursor-not-allowed disabled:opacity-60"
        >
          {enCours ? 'Préparation…' : 'Télécharger'}
        </button>
      </div>

      {erreur ? (
        <p role="alert" data-test={`erreur-${fichier.categorie}`} className="mt-2 text-sm text-score-actuel">
          {erreur}
        </p>
      ) : null}
    </li>
  )
}
